import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  Input,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useOrg } from '../context/OrgContext';

const ROLES = [
  { value: 'Owner', label: 'Owner' },
  { value: 'Driver', label: 'Driver (employee)' },
  { value: 'Contractor', label: 'Driver (1099 contractor)' },
  { value: 'Dispatcher', label: 'Dispatcher' },
  { value: 'Admin', label: 'Office / admin' },
  { value: 'AnsweringService', label: 'Answering service' },
  { value: 'Other', label: 'Other' }
];
const ACCEPTED = 'application/pdf,image/jpeg,image/png,image/heic,image/webp,.pdf,.jpg,.jpeg,.png,.heic,.webp';
const EMPTY_PERSON = { personName: '', employeeRole: 'Driver', email: '', phone: '', dateOfBirth: '' };

/**
 * Where the "Request background checks" email sends an applicant: it shows
 * how many checks we expect from their application, who is already on file,
 * and lets them add each employee or contractor with their document.
 */
function EmployeeBackgroundChecksPage() {
  const { name } = useOrg();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const apiBase = `${process.env.REACT_APP_API_URL}/api/applications/${id}/background-checks-public`;

  const [summary, setSummary] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [person, setPerson] = useState(EMPTY_PERSON);
  const [file, setFile] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);

  const load = useCallback(async () => {
    const response = await fetch(`${apiBase}?token=${encodeURIComponent(token)}`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'This link is invalid or has expired.');
    setSummary(data);
  }, [apiBase, token]);

  useEffect(() => {
    if (!token) {
      setLinkError(`This page needs the link from your email. Please open the "Provide Details and Upload Background Checks" link in the email from ${name}.`);
      return;
    }
    load().catch((err) => setLinkError(err.message));
  }, [token, load, name]);

  const openForm = () => {
    setPerson(EMPTY_PERSON);
    setFile(null);
    setFormError(null);
    setFormOpen(true);
  };

  const submit = async () => {
    if (!person.personName.trim()) return setFormError("Enter the person's name.");
    if (!file) return setFormError('Attach their background check (PDF or photo).');
    setSaving(true);
    setFormError(null);
    const body = new FormData();
    body.append('token', token);
    Object.entries(person).forEach(([key, value]) => body.append(key, value));
    body.append('backgroundCheckDoc', file);
    try {
      const response = await fetch(apiBase, { method: 'POST', body });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The upload failed. Please try again.');
      setLastAdded(person.personName.trim());
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, px: 2 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" gutterBottom>Background Checks</Typography>

        {linkError ? (
          <Alert severity="error">{linkError}</Alert>
        ) : !summary ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>{summary.companyName}</Typography>
            <Alert severity={summary.remaining > 0 ? 'info' : 'success'} sx={{ mb: 2 }}>
              {summary.remaining > 0
                ? `Your application lists ${summary.expected} ${summary.expected === 1 ? 'person' : 'people'} (owner and employees). We have ${summary.onFile} background ${summary.onFile === 1 ? 'check' : 'checks'} on file, so we still need ${summary.remaining}. Please also add any 1099 contractors who will run our calls.`
                : `We have background checks for all ${summary.expected} ${summary.expected === 1 ? 'person' : 'people'} listed on your application. You can still add anyone who is missing.`}
            </Alert>

            {lastAdded && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLastAdded(null)}>
                Background check for {lastAdded} received. Thank you!
              </Alert>
            )}

            <Typography variant="subtitle1" sx={{ mt: 1 }}>People on file</Typography>
            <Divider sx={{ mb: 1 }} />
            {summary.people.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                No one yet.
              </Typography>
            ) : (
              <List dense sx={{ mb: 2 }}>
                {summary.people.map((p, i) => (
                  <ListItem key={`${p.personName}-${i}`} disableGutters
                    secondaryAction={<Chip size="small" label={p.hasDocument ? 'Received' : 'Waiting for document'} color={p.hasDocument ? 'success' : 'default'} />}>
                    <ListItemText primary={p.personName} secondary={p.role || null} />
                  </ListItem>
                ))}
              </List>
            )}

            <Button variant="contained" fullWidth startIcon={<AddIcon />} onClick={openForm}>
              Add employee or contractor
            </Button>
          </>
        )}
      </Paper>

      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add employee or contractor</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField label="Full name" required fullWidth value={person.personName}
                onChange={(e) => setPerson({ ...person, personName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="role-label">Role</InputLabel>
                <Select labelId="role-label" label="Role" value={person.employeeRole}
                  onChange={(e) => setPerson({ ...person, employeeRole: e.target.value })}>
                  {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Date of birth" type="date" fullWidth InputLabelProps={{ shrink: true }} value={person.dateOfBirth}
                onChange={(e) => setPerson({ ...person, dateOfBirth: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email" type="email" fullWidth value={person.email}
                onChange={(e) => setPerson({ ...person, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Phone" fullWidth value={person.phone}
                onChange={(e) => setPerson({ ...person, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Input id="bc-file" type="file" inputProps={{ accept: ACCEPTED }} sx={{ display: 'none' }}
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <label htmlFor="bc-file">
                <Button variant="outlined" component="span" fullWidth startIcon={<CloudUploadIcon />}>
                  {file ? file.name : 'Attach background check (PDF or photo)'}
                </Button>
              </label>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'Uploading…' : 'Add person'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default EmployeeBackgroundChecksPage;
